import { useCallback, useEffect, useState } from "react";
import { fmt, setDateFormat } from "@/lib/status";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, pub, errMsg } from "@/lib/api";
import SigningFlow from "@/components/SigningFlow";
import { Ended, Receipt } from "@/pages/KioskPage";
import { LanguageBar, makeT, langFromPref } from "@/lib/i18n";
import { LargePrintToggle, useLargePrint } from "@/lib/largePrint";

export default function RemotePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ last_name: "", dob: "", signer_last_name: "" });
  const [msg, setMsg] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [lang, setLangRaw] = useState(langFromPref(navigator.language));
  const [picked, setPicked] = useState(false);
  const setLang = (l) => { setPicked(true); setLangRaw(l); };
  const lp = useLargePrint();
  const setLp = lp[1];
  const t = makeT(lang);
  const loadPacket = useCallback(() => pub.get(`/sign/${token}/packet`).then((r) => { if (r.data.receipt) setReceipt(r.data.receipt); else { setData(r.data); if (r.data.language && !picked) setLangRaw(r.data.language); } }).catch((e) => setError(errMsg(e))), [token, picked]);
  useEffect(() => {
    pub.get(`/sign/${token}`).then((r) => { setMeta(r.data); if (r.data.language) setLangRaw(r.data.language); setLp(!!r.data.large_print); if (r.data.receipt) setReceipt(r.data.receipt); else if (r.data.verified) loadPacket(); }).catch((e) => setError(errMsg(e)));
  }, [token, setLp]); // eslint-disable-line react-hooks/exhaustive-deps
  const verify = async (e) => {
    e.preventDefault();
    try { await pub.post(`/sign/${token}/verify`, f); loadPacket(); } catch (er) { setMsg(errMsg(er)); }
  };
  if (receipt) return (
    <Receipt r={receipt} lang={lang}>
      {receipt.status === "signed" && <a href={`${API}/sign/${token}/copy`} className="inline-block mt-6 underline text-blue-700 text-lg" data-testid="remote-download-copy">{t("Download your signed copy")}</a>}
      <p className="mt-4 text-sm text-slate-500">{t("You can close this page.")}</p>
    </Receipt>
  );
  if (error) return <Ended title={t("This link can't be used")} body={error} />;
  if (!meta) return <Ended title={t("Loading...")} body="" />;
  setDateFormat(meta.practice?.date_format);
  if (data) return <SigningFlow key={data.documents.map((d) => d.template_id).join(",")} token={token} data={data} onFinished={setReceipt} lang={lang} setLang={setLang} lp={lp} onReload={loadPacket} />;
  return (
    <div className="min-h-screen font-kiosk flex items-center justify-center p-6 ace-watermark" style={{ background: "#FAF8F5" }} data-testid="remote-challenge">
      <form onSubmit={verify} className="w-full max-w-md bg-white rounded-2xl border p-8 fade-in">
        <div className="flex flex-wrap items-center gap-2 mb-4"><img src="/ace-mark.png" alt="ACE eSIGN" className="h-10 w-auto mr-auto" data-testid="remote-brand-mark" /><LargePrintToggle lp={lp} label={t("Large text")} /></div>
        <div className="mb-6"><LanguageBar lang={lang} setLang={setLang} t={t} compact /></div>
        <h1 className="text-3xl font-semibold">{t("Confirm your identity")}</h1>
        <p className="text-slate-600 mt-2">{meta.practice.legal_entity} {t("remote_intro")}</p>
        <Label className="block mt-6" htmlFor="rc-ln">{t("Patient last name")}</Label>
        <Input id="rc-ln" className="h-12 mt-1 text-lg" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="challenge-last-name" />
        <Label className="block mt-4" htmlFor="rc-dob">{t("Patient date of birth")}</Label>
        <Input id="rc-dob" type="date" className="h-12 mt-1 text-lg" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="challenge-dob" />
        <Label className="block mt-4" htmlFor="rc-sl">{t("rep_last_name")}</Label>
        <Input id="rc-sl" className="h-12 mt-1 text-lg" value={f.signer_last_name} onChange={(e) => setF({ ...f, signer_last_name: e.target.value })} data-testid="challenge-signer-last-name" />
        {msg && <p className="mt-4 text-red-700" role="alert" data-testid="challenge-error">{msg}</p>}
        <Button type="submit" className="w-full h-14 text-lg mt-6 bg-[#0B3D8C] hover:bg-[#082F6B]" disabled={!f.last_name || !f.dob} data-testid="challenge-submit">{t("Continue")}</Button>
        <p className="text-xs text-slate-400 mt-4">{t("attempts_note")} {fmt(meta.expires_at)}.</p>
      </form>
    </div>
  );
}
